import React, { useState, useMemo, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

function MiniCalendar({ 
  data, 
  selectedYear, 
  selectedMonth, 
  onYearChange, 
  onMonthChange 
}) {
  const [showYearDropdown, setShowYearDropdown] = useState(false)
  const yearSelectorRef = useRef(null)

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  // Get available years from conference data
  const availableYears = useMemo(() => {
    const currentDate = new Date()
    if (!data?.conferences) return [currentDate.getFullYear()]
    const years = new Set()
    data.conferences.forEach(conf => {
      if (conf.year) years.add(conf.year)
    })
    return Array.from(years).sort((a, b) => a - b)
  }, [data])

  const handleYearSelect = (year) => {
    onYearChange(year)
    setShowYearDropdown(false)
  }

  const handleYearNavigation = (direction) => {
    const newYear = selectedYear + direction
    // Only navigate if the new year is within the available data range
    if (availableYears.includes(newYear)) {
      onYearChange(newYear)
    }
  }

  const handleClickOutside = (e) => {
    if (yearSelectorRef.current && !yearSelectorRef.current.contains(e.target)) {
      setShowYearDropdown(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowYearDropdown(false)
    }
  }

  // Close dropdown when clicking outside or pressing ESC
  useEffect(() => {
    if (showYearDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [showYearDropdown])

  return (
    <div className="mini-calendar">
      <div className="year-navigation">
        <button onClick={() => handleYearNavigation(-1)} className="year-nav-btn">
          <ChevronLeft size={20} />
        </button>
        <div className="year-selector-wrapper" ref={yearSelectorRef}>
          <button 
            className="year-display clickable" 
            onClick={() => setShowYearDropdown(!showYearDropdown)}
          >
            {selectedYear}
          </button>
          {showYearDropdown && (
            <div className="year-dropdown">
              {availableYears.map(year => (
                <button
                  key={year}
                  className={`year-option ${year === selectedYear ? 'active' : ''}`}
                  onClick={() => handleYearSelect(year)}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => handleYearNavigation(1)} className="year-nav-btn">
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="month-grid">
        {monthNames.map((month, index) => (
          <button
            key={month}
            className={`month-button ${index === selectedMonth ? 'active' : ''}`}
            onClick={() => onMonthChange(index)}
          >
            {month.substring(0, 3)}
          </button>
        ))}
      </div>
    </div>
  )
}

export default MiniCalendar